/**
 * Asset Export Component
 */

import { useState, useCallback } from "react";
import {
  AssetFilter,
  ExportFormat,
  AssetExportResult,
  ASSET_TYPE_LABELS,
  ASSET_STATUS_LABELS,
} from "../../types/asset-types";
import { useAssets } from "../../hooks/use-assets";

export interface ExportHistory {
  id: string;
  filename: string;
  format: ExportFormat;
  recordCount: number;
  filter: AssetFilter;
  createdAt: string;
  downloadUrl?: string;
  expiresAt?: string;
  status: "pending" | "completed" | "failed" | "expired";
}

export interface AssetExportProps {
  filter: AssetFilter;
  totalAssets: number;
  onClose: () => void;
}

export function AssetExport({
  filter,
  totalAssets,
  onClose,
}: AssetExportProps) {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>(
    ExportFormat.CSV
  );
  const [selectedFields, setSelectedFields] = useState<string[]>([
    "name",
    "type",
    "status",
    "createdAt",
    "venue",
    "fileCount",
    "conversationCount",
  ]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportHistory, setExportHistory] = useState<ExportHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const { exportAssets } = useAssets();

  // Available export fields
  const availableFields = [
    { key: "name", label: "Asset Name", required: true },
    { key: "type", label: "Asset Type", required: false },
    { key: "status", label: "Status", required: false },
    { key: "createdAt", label: "Created Date", required: false },
    { key: "updatedAt", label: "Updated Date", required: false },
    { key: "createdBy", label: "Created By", required: false },
    { key: "venue", label: "Venue", required: false },
    { key: "venueType", label: "Venue Type", required: false },
    { key: "venueAddress", label: "Venue Address", required: false },
    { key: "fileCount", label: "File Count", required: false },
    { key: "fileNames", label: "File Names", required: false },
    { key: "fileSizes", label: "File Sizes", required: false },
    { key: "conversationCount", label: "Conversation Count", required: false },
    { key: "conversationIds", label: "Conversation IDs", required: false },
    { key: "metadata", label: "Metadata", required: false },
    { key: "location", label: "Location (for stationary)", required: false },
    { key: "vehicleInfo", label: "Vehicle Info (for mobile)", required: false },
    {
      key: "publicationInfo",
      label: "Publication Info (for print)",
      required: false,
    },
    {
      key: "platformInfo",
      label: "Platform Info (for digital)",
      required: false,
    },
    {
      key: "seatingInfo",
      label: "Seating Info (for seating)",
      required: false,
    },
  ];

  // Load export history from localStorage
  useState(() => {
    const saved = localStorage.getItem("asset-export-history");
    if (saved) {
      try {
        const history = JSON.parse(saved);
        // Filter out expired exports
        const validHistory = history.filter((item: ExportHistory) => {
          if (item.expiresAt) {
            return new Date(item.expiresAt) > new Date();
          }
          return true;
        });
        setExportHistory(validHistory);
      } catch (error) {
        console.error("Error loading export history:", error);
      }
    }
  });

  // Handle field selection
  const handleFieldToggle = (fieldKey: string) => {
    const field = availableFields.find((f) => f.key === fieldKey);
    if (field?.required) return; // Can't deselect required fields

    setSelectedFields((prev) =>
      prev.includes(fieldKey)
        ? prev.filter((f) => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  // Handle export
  const handleExport = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);

    try {
      const result: AssetExportResult = await exportAssets(
        filter,
        selectedFormat
      );

      // Create export history entry
      const historyEntry: ExportHistory = {
        id: Date.now().toString(),
        filename: result.filename,
        format: selectedFormat,
        recordCount: result.recordCount,
        filter,
        createdAt: new Date().toISOString(),
        downloadUrl: result.downloadUrl,
        expiresAt: result.expiresAt,
        status: "completed",
      };

      // Update history
      const updatedHistory = [historyEntry, ...exportHistory];
      setExportHistory(updatedHistory);
      localStorage.setItem(
        "asset-export-history",
        JSON.stringify(updatedHistory)
      );

      // Start download
      const link = document.createElement("a");
      link.href = result.downloadUrl;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message
      alert(`Export completed! ${result.recordCount} assets exported.`);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, exportAssets, filter, selectedFormat, exportHistory]);

  // Download from history
  const handleDownloadFromHistory = (historyItem: ExportHistory) => {
    if (!historyItem.downloadUrl) return;

    const link = document.createElement("a");
    link.href = historyItem.downloadUrl;
    link.download = historyItem.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Clear export history
  const handleClearHistory = () => {
    setExportHistory([]);
    localStorage.removeItem("asset-export-history");
  };

  // Format filter summary
  const getFilterSummary = () => {
    const parts: string[] = [];

    if (filter.type) {
      parts.push(`Type: ${ASSET_TYPE_LABELS[filter.type]}`);
    }

    if (filter.status) {
      parts.push(`Status: ${ASSET_STATUS_LABELS[filter.status]}`);
    }

    if (filter.search) {
      parts.push(`Search: "${filter.search}"`);
    }

    if (filter.venueId) {
      parts.push("Venue: Selected");
    }

    if (filter.createdFrom || filter.createdTo) {
      parts.push("Date Range: Selected");
    }

    if (filter.conversationId) {
      parts.push(`Conversation: ${filter.conversationId}`);
    }

    if (filter.tags && filter.tags.length > 0) {
      parts.push(`Tags: ${filter.tags.join(", ")}`);
    }

    return parts.length > 0 ? parts.join(" • ") : "All assets";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Export Assets</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Export Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Export Summary</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Assets to export:</span>{" "}
                {totalAssets}
              </p>
              <p>
                <span className="font-medium">Filters applied:</span>{" "}
                {getFilterSummary()}
              </p>
            </div>
          </div>

          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Export Format
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="format"
                  value={ExportFormat.CSV}
                  checked={selectedFormat === ExportFormat.CSV}
                  onChange={(e) =>
                    setSelectedFormat(e.target.value as ExportFormat)
                  }
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">CSV</div>
                  <div className="text-xs text-gray-500">
                    Comma-separated values, compatible with Excel
                  </div>
                </div>
              </label>
              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="format"
                  value={ExportFormat.EXCEL}
                  checked={selectedFormat === ExportFormat.EXCEL}
                  onChange={(e) =>
                    setSelectedFormat(e.target.value as ExportFormat)
                  }
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">Excel</div>
                  <div className="text-xs text-gray-500">
                    Native Excel format with formatting
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Field Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Fields to Export
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {availableFields.map((field) => (
                <label key={field.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(field.key)}
                    onChange={() => handleFieldToggle(field.key)}
                    disabled={field.required}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span
                    className={`ml-2 text-sm ${
                      field.required
                        ? "text-gray-900 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    {field.label}
                    {field.required && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              * Required fields cannot be deselected
            </p>
          </div>

          {/* Export History Toggle */}
          {exportHistory.length > 0 && (
            <div>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center text-sm text-blue-600 hover:text-blue-700"
              >
                <svg
                  className={`w-4 h-4 mr-1 transform transition-transform ${
                    showHistory ? "rotate-90" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Export History ({exportHistory.length})
              </button>
            </div>
          )}

          {/* Export History */}
          {showHistory && exportHistory.length > 0 && (
            <div className="border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <h4 className="font-medium text-gray-900">Recent Exports</h4>
                <button
                  onClick={handleClearHistory}
                  className="text-xs text-red-600 hover:text-red-700"
                >
                  Clear History
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto">
                {exportHistory.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {item.filename}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.recordCount} assets • {item.format} •{" "}
                        {new Date(item.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    {item.downloadUrl && item.status === "completed" && (
                      <button
                        onClick={() => handleDownloadFromHistory(item)}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Download
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {selectedFields.length} fields selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || selectedFields.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isExporting && (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isExporting ? "Exporting..." : "Export Assets"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
